import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authAPI, setTokens, clearTokens } from '../services/api';

interface MFAPending {
  mfa_required: true;
  mfa_token: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<MFAPending | void>;
  completeMFA: (mfaToken: string, totpCode: string) => Promise<User>;
  logout: () => Promise<void>;
  organisationId: number | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // ISSUE-01: On startup, restore session via /auth/me using the httpOnly cookie.
  // No localStorage reads â€” tokens are never persisted in JS-accessible storage.
  useEffect(() => {
    authAPI.me()
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<MFAPending | void> => {
    const form = new URLSearchParams();
    form.append('username', email);
    form.append('password', password);

    const res = await authAPI.login(form);

    // MFA enforcement (ISSUE-08): server signals a TOTP challenge is required
    if (res.data.mfa_required) {
      return { mfa_required: true, mfa_token: res.data.mfa_token } as MFAPending;
    }

    // Store tokens from response body (cookies are blocked cross-domain)`n    const { access_token, refresh_token } = res.data;`n    setTokens(access_token, refresh_token);`n`n    // Fetch user profile to populate state
    const meRes = await authAPI.me();
    setUser(meRes.data);
  };  const completeMFA = async (mfaToken: string, totpCode: string): Promise<User> => {
    // MFA challenge response now includes user data directly
    const response = await authAPI.mfaChallenge(mfaToken, totpCode);
    const { access_token, refresh_token, user: userData } = response.data;
    
    // Store tokens from response body
    setTokens(access_token, refresh_token);
    
    setUser(userData);
    return userData;
  };

  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();  // server revokes refresh token
    } catch {
      // Proceed with local state clear even if server call fails
    }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      completeMFA,
      logout,
      organisationId: user?.organisation_id ?? null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

